import React, { FC, useContext, useEffect, useRef, useState, useCallback, useMemo } from "react";
import { initials } from "../../../helpers/Inicials";
import { dateToNiceString } from "../../../helpers/dateToNiceString";
import ExitMark from "../../../assets/ButtonIcons/user-monitor-exit-button.svg";
import "./TaskDescription.css";
import { Context } from "../../../index";
import RightArrow from "../../../assets/rightArrow.svg";
import { IFunctionPermissions } from "../../../common/api_interfaces/role/IFunctionPermissions";
import FunctionSystem from "../../../models/functionCode/functionsSystem";
import HtmlPreview from "../../UiLib/HtmlPreview/HtmlPreview";
import axios from "axios";
import { AppContext } from "../../../App";
import { hasPermission } from "../../../helpers/hasPermisson";
import { RenderValue } from "../../Shared/HistoryTable/ProjectHistoryMapper";
import { IProjectHistoryDisplay } from "../../../common/api_interfaces/history/IProjectHistoryDisplay";
import { IGetTaskHistoryDisplay } from "../../../common/api_interfaces/history/IGetTaskHistoryDisplay";
import { IMonitorTaskDisplay } from "../../../common/api_interfaces/monitor/IUserMonitorTaskFullInfo";
import { FunctionsAccessRequest } from "../../../common/api_interfaces/role/FunctionsAccessRequest";

interface ITaskDescriptionProps {
    task: IMonitorTaskDisplay;
    setSelectedTaskId: (taskId: number | undefined) => void;
}

const OVERFLOW_HEIGHT = 200;

const TaskDescription: FC<ITaskDescriptionProps> = ({ task, setSelectedTaskId }) => {
    const { store } = useContext(Context);
    const { showToast } = useContext(AppContext);
    
    const [showToggleComment, setShowToggleComment] = useState(false);
    const [historyTask, setHistoryTask] = useState<IProjectHistoryDisplay[]>([]);
    const [scrollEndedHistory, setScrollEndedHistory] = useState(false);
    const [userAccess, setUserAccess] = useState<IFunctionPermissions[]>([]);
    const [closedRows, setClosedRows] = useState<string[]>([]);
    const [processedCommentText, setProcessedCommentText] = useState<string | null>(null);
    
    const commentRef = useRef<HTMLDivElement>(null);
    const descriptionRef = useRef<HTMLDivElement>(null);

    // Мемоизированные значения для производительности
    const viewTask = useMemo(() => hasPermission(userAccess, "TaskAction", "view"), [userAccess]);
    const viewChat = useMemo(() => hasPermission(userAccess, "TaskChatAction", "view"), [userAccess]);
    const viewFile = useMemo(() => hasPermission(userAccess, "TaskFileAction", "view"), [userAccess]);

    const onMinimizeButtonClick = useCallback((elem: string) => {
        setClosedRows(prev => prev.some(r => r === elem) 
            ? prev.filter(r => r !== elem)
            : [...prev, elem]
        );
    }, []);

    // Обработка прав доступа
    useEffect(() => {
        const checkPermissions = async () => {
            try {
                const functionClass = new FunctionSystem();
                const newCheckRole: FunctionsAccessRequest = {
                    projectId: task?.project.id,
                    boardId: null,
                    functionCodes: functionClass.getTaskFunction,
                };

                const checkThisRole = await store.hasPermission(newCheckRole);
                setUserAccess(checkThisRole);
            } catch (error) {
                console.error("Permission check error:", error);
            }
        };

        checkPermissions();
    }, [task, store]);

    // Обработка комментариев при отсутствии прав на файлы
    useEffect(() => {
        if (!viewFile && task.lastComments[0]?.text) {
            const processCommentText = (text: string): string => {
                let result = text;
                const hrefRegex = /href="[^"]*"/g;
                return result.replace(hrefRegex, '');
            };

            setProcessedCommentText(processCommentText(task.lastComments[0].text));
        } else {
            setProcessedCommentText(null);
        }
    }, [viewFile, task.lastComments[0]?.text]);

    // Проверка высоты элементов
    const checkElementsHeight = useCallback(() => {
        if (commentRef.current) {
            setShowToggleComment(commentRef.current.scrollHeight > OVERFLOW_HEIGHT);
        }
    }, []);

    // Загрузка истории задачи
    useEffect(() => {
        const loadTaskHistory = async () => {
            if (!viewTask) return;

            const getHistory: IGetTaskHistoryDisplay = {
                skip: 0,
                take: 3,
                filters: { taskId: task.id },
                sort: { date: "desc" },
            };

            try {
                const response = await store.taskService.getTaskHistory(getHistory);
                setHistoryTask(response.data);
            } catch (error) {
                if (axios.isAxiosError(error)) {
                     showToast(error.response?.data?.message, error.response?.data?.details);
                }
            }
        };

        loadTaskHistory();
        checkElementsHeight();

        window.addEventListener("resize", checkElementsHeight);
        return () => window.removeEventListener("resize", checkElementsHeight);
    }, [task.id, viewTask, store.taskService, showToast, checkElementsHeight]);

    const appendTeamMembers = useCallback(async () => {
        if (scrollEndedHistory) return;

        const getHistory: IGetTaskHistoryDisplay = {
            skip: historyTask.length,
            take: 5,
            filters: { taskId: task.id },
            sort: { date: "asc" },
        };

        try {
            const response = await store.taskService.getTaskHistory(getHistory);
            const data = response.data;

            if (data.length > 0) {
                setHistoryTask(prev => [...prev, ...data]);
            } else {
                setScrollEndedHistory(true);
            }
        } catch (error) {
            if (axios.isAxiosError(error)) {
                showToast(error.response?.data?.message);
            }
            setScrollEndedHistory(true);
        }
    }, [historyTask.length, task.id, store.taskService, showToast, scrollEndedHistory]);

    const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
        const { scrollTop, offsetHeight, scrollHeight } = e.currentTarget;
        if (scrollTop + offsetHeight >= scrollHeight - 1 && !scrollEndedHistory) {
            appendTeamMembers();
        }
    }, [appendTeamMembers, scrollEndedHistory]);

    const renderHistory = useMemo(() => {
        if (!historyTask.length) return null;

        return historyTask.map((item, index) => (
            <div key={index} className="user-monitor-right-bar-block-history">
                <div className="user-monitor-right-bar-block-history-head">
                    <div className="user-monitor-right-bar-block-history_name">
                        {initials(`${item.createdBy.surname} ${item.createdBy.name} ${item.createdBy.middlename}`)}
                    </div>
                </div>
                <div className="user-monitor-right-bar-block-history_date">
                    {dateToNiceString(new Date(item.createdAt), "month dd, yyyy")}
                </div>
                <div className="user-monitor-right-bar-block-history_data">
                    {item.changeDataDifference.map((diff) => (
                        <div
                            key={diff.caption || diff.type}
                            className="user-monitor-right-bar-block-history_data-block"
                            style={{
                                display: "flex",
                                flexWrap: "wrap",
                                wordBreak: "break-all",
                                alignItems: "center",
                            }}
                        >
                            {diff.caption && (
                                <span style={{ fontWeight: "400", marginRight: "5px", width: "100px" }}>
                                    {diff.caption}
                                </span>
                            )}
                            <RenderValue
                                type={diff.type}
                                value={diff.oldValue}
                                showFullChanges={false}
                                minimized={!closedRows.includes(`${index}${diff.caption}old`)}
                                onMinimizeButtonClick={() => onMinimizeButtonClick(`${index}${diff.caption}old`)}
                            />
                            {(diff.oldValue || diff.newValue) && (
                                <img
                                    style={{ alignSelf: "center" }}
                                    src={RightArrow}
                                    alt=""
                                    height={10}
                                />
                            )}
                            <RenderValue
                                type={diff.type}
                                value={diff.newValue}
                                showFullChanges={false}
                                minimized={!closedRows.includes(`${index}${diff.caption}new`)}
                                onMinimizeButtonClick={() => onMinimizeButtonClick(`${index}${diff.caption}new`)}
                            />
                        </div>
                    ))}
                </div>
            </div>
        ));
    }, [historyTask, closedRows, onMinimizeButtonClick]);

    // Рендер последнего комментария
    const renderLastComment = useMemo(() => {
        if (!viewChat) {
            return (
                <div className="user-monitor-right-bar-block-hide">
                    <h2>У вас нет прав на просмотр комментариев</h2>
                </div>
            );
        }

        if (!task.lastComments[0]) {
            return (
                <div className="user-monitor-right-bar-block-hide">
                    <h2>Нет комментариев</h2>
                </div>
            );
        }

        return task.lastComments.map((comment, index) => {
            const commentText = processedCommentText !== null ? processedCommentText : comment.text;

            return (
                <div key={index} ref={commentRef} className="current-block">
                    <div className="comment-author">
                        <div className="author">
                            <div className="name">
                                {initials(`${comment.author.surname} ${comment.author.name} ${comment.author.middlename}`)}
                            </div>
                            <div className="relation-to-task">
                                {comment.author.positionInRelationToTask}
                            </div>
                        </div>
                        <div className="date">
                            {dateToNiceString(new Date(comment.date), "month dd, yyyy")}
                        </div>
                    </div>
                    <div className='comment'>
                        <HtmlPreview content={commentText} />
                    </div>
                </div>
            );
        });
    }, [viewChat, task.lastComments, showToggleComment, processedCommentText]);

    if (!viewTask) {
        return (
            <div className="user-monitor-right-bar-block-hide">
                <h2>У вас нет прав на просмотр задачи!</h2>
            </div>
        );
    }

    return (
        <div className="task-description-container">
            <div className="task-description-header">
                <p className="task-description-name">{task.name}</p>
                <div className="exit-button" onClick={() => setSelectedTaskId(undefined)}>
                    <img src={ExitMark} alt="Закрыть задачу" />
                </div>
            </div>            
            <div className="task-description-desc">
                <p className="block-name">Описание</p>
                <div className="task-description-quill">
                    <div
                        ref={descriptionRef}
                        className={`block-text history-table-markdown-img`}
                    >
                        <HtmlPreview content={task.description ?? ""} />
                    </div>
                </div>
            </div>
            <div className="task-description-comment">
                <p className="block-name">ПОСЛЕДНИЕ КОММЕНТАРИИ</p>
                <div className="last-comments-block">
                    {renderLastComment}
                </div>
            </div>
            <div className="task-description-history">
                <p className="block-name">ПОСЛЕДНИЕ ИЗМЕНЕНИЯ</p>
                <div 
                    className="task-description-block-history"
                    onScroll={handleScroll}
                >
                    {renderHistory}
                </div>
            </div>
        </div>
    );
};

export default TaskDescription;